import { useEffect, useState } from 'react';
import { AlertCircle, Check, Clock, Loader, Tag, Package, DollarSign, Droplet, Plus, X, Search } from 'lucide-react';

interface ProductData {
    name: string;
    category_code?: string;
    brand_name?: string;
    base_shop_id?: number;
    base_unit_id?: number;
}

interface PromotionData {
    promotion_type?: string;
    value?: number;
    currency?: string;
    discount_percentage?: number;
    original_price?: number;
    discounted_price?: number;
    starts_at?: string | number;
    ends_at?: string | number;
}

interface PackagingData {
    name?: string;
    size_value?: string;
    size_unit?: string;
    multiplier?: number;
}

interface PriceData {
    price_type_code: string;
    value: number;
    starts_at?: string;
    ends_at?: string;
    description?: string;
}

interface DepositData {
    deposit_type?: string;
    price?: number;
    currency?: string;
}

interface Category {
    id: number;
    code: string;
    name?: string;
    hierarchy?: Array<{
        id: number;
        code: string;
        name?: string;
    }>;
}

interface Shop {
    id: number;
    name: string;
}

interface Unit {
    id: number;
    name: string;
    abbreviation: string;
}

interface Brand {
    id: number;
    name: string;
}

interface ExtPromotionStaging {
    id: number;
    source: string;
    status: 'pending' | 'processing' | 'completed' | 'error';
    product_data: ProductData;
    promotion_data: PromotionData;
    packaging_data: PackagingData;
    prices_data: PriceData[];
    deposit_data: DepositData;
    error_message?: string;
    created_base_product_id?: number;
    created_by?: string;
    processed_at?: string;
    created_at: string;
    updated_at: string;
    existing_product?: {
        id: number;
        name: string;
        brand_name?: string;
        base_shop_name?: string;
        category?: string;
    } | null;
}

const statusConfig = {
    pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
    processing: { color: 'bg-blue-100 text-blue-800', icon: Loader },
    completed: { color: 'bg-green-100 text-green-800', icon: Check },
    error: { color: 'bg-red-100 text-red-800', icon: AlertCircle },
};

const ExtPromotions = () => {
    const [stagings, setStagings] = useState<ExtPromotionStaging[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [editingStaging, setEditingStaging] = useState<number | null>(null);
    const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
    const [categorySearchQuery, setCategorySearchQuery] = useState('');
    const [categories, setCategories] = useState<Category[]>([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [creatingProduct, setCreatingProduct] = useState(false);
    const [shops, setShops] = useState<Shop[]>([]);
    const [units, setUnits] = useState<Unit[]>([]);
    const [brands, setBrands] = useState<Brand[]>([]);
    const [selectedShop, setSelectedShop] = useState<Shop | null>(null);
    const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
    const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
    const [brandSearchQuery, setBrandSearchQuery] = useState('');
    const [showBrandDropdown, setShowBrandDropdown] = useState(false);
    const [creatingBrand, setCreatingBrand] = useState(false);
    const [suggestedUnit, setSuggestedUnit] = useState<Unit | null>(null);

    useEffect(() => {
        fetchStagings();
        fetchShopsAndUnits();
        fetchBrands();
    }, []);

    // Boltok, egységek és márkák betöltése
    const fetchBrands = async () => {
        try {
            const response = await fetch('/api/admin/brands');
            if (response.ok) {
                const data = await response.json();
                setBrands(data.brands || []);
            }
        } catch (err) {
            console.error('Hiba a márkák betöltéskor:', err);
        }
    };

    // Boltok és egységek betöltése
    const fetchShopsAndUnits = async () => {
        try {
            const [shopsRes, unitsRes] = await Promise.all([
                fetch('/api/admin/shops'),
                fetch('/api/admin/units')
            ]);

            if (shopsRes.ok) {
                const data = await shopsRes.json();
                setShops(data.shops || []);
            }

            if (unitsRes.ok) {
                const data = await unitsRes.json();
                setUnits(data.units || []);
            }
        } catch (err) {
            console.error('Hiba a boltok/egységek betöltéskor:', err);
        }
    };

    // Kategória keresése
    useEffect(() => {
        if (categorySearchQuery.length < 2) {
            setCategories([]);
            return;
        }

        const timer = setTimeout(async () => {
            try {
                const response = await fetch(`/api/admin/categories/search_fine?q=${encodeURIComponent(categorySearchQuery)}`);
                if (response.ok) {
                    const data = await response.json();
                    setCategories(data.categories || []);
                    setShowCategoryDropdown(true);
                }
            } catch (error) {
                console.error('Hiba a kategóriák kereséskor:', error);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [categorySearchQuery]);

    // Márkák keresése
    useEffect(() => {
        if (brandSearchQuery.length < 2) {
            setShowBrandDropdown(false);
            return;
        }

        const timer = setTimeout(() => {
            const filtered = brands.filter(b =>
                b.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
            );
            // Ha nincs találat, akkor megmutatjuk a lehetőséget hogy létrehozhassuk
            setShowBrandDropdown(true);
        }, 300);

        return () => clearTimeout(timer);
    }, [brandSearchQuery, brands]);

    // Javasolt unit meghatározása a csomagolás alapján
    const getSuggestedUnitFromPackaging = (staging: ExtPromotionStaging) => {
        const sizeUnit = staging.packaging_data?.size_unit?.toLowerCase();
        if (!sizeUnit) return null;

        // Keressük meg az alapértelmezett unit-et
        let unitName = '';
        if (sizeUnit === 'g') unitName = 'KG';
        if (sizeUnit === 'l') unitName = 'LT';
        if (sizeUnit === 'ml') unitName = 'LT';
        if (sizeUnit === 'kg') unitName = 'KG';
        if (sizeUnit === 'kt') unitName = 'KT';

        const suggested = units.find(u => u.abbreviation === unitName);
        return suggested || null;
    };

    const fetchStagings = async () => {
        try {
            setLoading(true);
            const response = await fetch('/api/admin/ext-promotion-stagings');
            if (!response.ok) throw new Error('Failed to fetch stagings');
            const data = await response.json();
            setStagings(Array.isArray(data) ? data : data.data || []);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error fetching stagings:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreateProduct = async (staging: ExtPromotionStaging) => {
        if (!selectedCategory) {
            setError('Kérjük, válasszon egy kategóriát!');
            return;
        }

        if (!selectedUnit) {
            setError('Kérjük, válasszon egy mértékegységet!');
            return;
        }

        try {
            setCreatingProduct(true);
            const response = await fetch('/api/admin/products', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    product: {
                        name: staging.product_data.name,
                        category_code: selectedCategory.code,
                        brand_name: selectedBrand ? undefined : staging.product_data.brand_name,
                        brand_id: selectedBrand?.id,
                        base_shop_id: selectedShop?.id || staging.product_data.base_shop_id,
                        base_unit_id: selectedUnit.id,
                    },
                    ext_promotion_staging_id: staging.id,
                }),
            });

            if (!response.ok) {
                const errData = await response.json();

                // Duplikátum esetén speciális üzenet
                if (response.status === 409 && errData.existing_product_id) {
                    const existing = errData.existing_product;
                    throw new Error(
                        `⚠️ Ez a termék már létezik!\n\n` +
                        `Név: ${existing.name}\n` +
                        `Márka: ${existing.brand_name || 'Nincs'}\n` +
                        `Alap bolt: ${existing.base_shop_name || 'Nincs'}\n\n` +
                        `Termék ID: ${errData.existing_product_id}`
                    );
                }

                throw new Error(errData.error || 'Hiba a termék létrehozásakor');
            }

            setError(null);
            alert('Termék sikeresen létrehozva!');
            setEditingStaging(null);
            setSelectedCategory(null);
            setCategorySearchQuery('');
            setSelectedShop(null);
            setSelectedUnit(null);
            setSelectedBrand(null);
            setBrandSearchQuery('');
            setSuggestedUnit(null);
            await fetchStagings();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error creating product:', err);
        } finally {
            setCreatingProduct(false);
        }
    };

    const handleCreateBrand = async () => {
        if (brandSearchQuery.trim().length < 2) {
            setError('Kérjük, legalább 2 karaktert írjon be!');
            return;
        }

        try {
            setCreatingBrand(true);
            const response = await fetch('/api/admin/brands', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ brand: { name: brandSearchQuery.trim() } }),
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Hiba a márka létrehozásakor');
            }

            const data = await response.json();
            const newBrand = data.brand;

            // A márkak frissítéséhez
            setBrands([...brands, newBrand]);
            setSelectedBrand(newBrand);
            setBrandSearchQuery('');
            setShowBrandDropdown(false);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error creating brand:', err);
        } finally {
            setCreatingBrand(false);
        }
    };

    const handleDeleteStaging = async (stagingId: number) => {
        if (!confirm('Biztosan szeretnéd törölni ezt az adatot?')) {
            return;
        }

        try {
            const response = await fetch(`/api/admin/ext-promotion-stagings/${stagingId}`, {
                method: 'DELETE',
                headers: { 'Content-Type': 'application/json' },
            });

            if (!response.ok) {
                const errData = await response.json();
                throw new Error(errData.error || 'Hiba az adatok törléskor');
            }

            setError(null);
            alert('Adatok sikeresen törölve!');
            await fetchStagings();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            console.error('Error deleting staging:', err);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="text-center">
                    <Loader className="h-8 w-8 animate-spin mx-auto mb-2" />
                    <p>Az adatok betöltése...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 max-w-7xl mx-auto">
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Chrome Extension - Promóciók</h1>
                <p className="text-gray-600 dark:text-gray-400">Staging adatbázisban lévő promóciókat</p>
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-50 dark:bg-red-900 border border-red-200 dark:border-red-700 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                        <p className="font-semibold text-red-800 dark:text-red-200">Hiba</p>
                        <p className="text-red-700 dark:text-red-300">{error}</p>
                    </div>
                </div>
            )}

            {stagings.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 rounded-lg">
                    <p className="text-gray-500 dark:text-gray-400">Nincsenek staging adatok</p>
                </div>
            ) : (
                <div className="grid gap-6">
                    {stagings.map((staging) => {
                        const StatusIcon = statusConfig[staging.status].icon;
                        const { color } = statusConfig[staging.status];

                        return (
                            <div key={staging.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                                {/* Header */}
                                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-700 p-4 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className={`p-2 rounded-lg ${color}`}>
                                            <StatusIcon className="h-4 w-4" />
                                        </div>
                                        <div>
                                            <p className="font-semibold text-gray-900 dark:text-white">{staging.product_data.name}</p>
                                            <p className="text-xs text-gray-600 dark:text-gray-400">ID: {staging.id}</p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${color}`}>
                                            {staging.status.toUpperCase()}
                                        </span>
                                        <button
                                            onClick={() => handleDeleteStaging(staging.id)}
                                            className="p-2 hover:bg-red-100 dark:hover:bg-red-900/40 rounded text-red-600 dark:text-red-400 transition"
                                            title="Törlés"
                                        >
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="p-4 space-y-4">
                                    {/* Termék adatok */}
                                    {Object.keys(staging.product_data).length > 0 && (
                                        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                                                <h3 className="font-semibold text-blue-900 dark:text-blue-100">Termék adatok</h3>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm mb-4">
                                                {Object.entries(staging.product_data).map(([key, value]) => (
                                                    <div key={key}>
                                                        <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                                                        <span className="ml-1 font-medium text-gray-900 dark:text-white">{String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>

                                            {/* Figyelmeztetés meglévő termékről */}
                                            {editingStaging === staging.id && staging.existing_product && (
                                                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/40 border border-red-400 dark:border-red-700 rounded text-red-800 dark:text-red-200 text-sm">
                                                    <div className="flex items-start gap-2">
                                                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                                                        <div>
                                                            <div className="font-semibold">⚠️ Ez a termék már létezik!</div>
                                                            <div className="mt-1">
                                                                <strong>{staging.existing_product.name}</strong>
                                                                {staging.existing_product.brand_name && <div>Márka: {staging.existing_product.brand_name}</div>}
                                                                {staging.existing_product.base_shop_name && <div>Bolt: {staging.existing_product.base_shop_name}</div>}
                                                                <div className="text-xs mt-1">Termék ID: #{staging.existing_product.id}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {editingStaging === staging.id && (
                                                <div className="border-t border-blue-200 dark:border-blue-800 pt-4">
                                                    <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                                        Kategória kiválasztása
                                                    </label>
                                                    <div className="relative mb-3">
                                                        <div className="flex gap-2">
                                                            <input
                                                                type="text"
                                                                placeholder="Kategória keresése..."
                                                                value={categorySearchQuery}
                                                                onChange={(e) => setCategorySearchQuery(e.target.value)}
                                                                className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                                                            />
                                                            {selectedCategory && (
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedCategory(null);
                                                                        setCategorySearchQuery('');
                                                                    }}
                                                                    className="px-2 py-2 hover:bg-blue-200 dark:hover:bg-blue-800 rounded"
                                                                >
                                                                    <X className="h-4 w-4" />
                                                                </button>
                                                            )}
                                                        </div>

                                                        {categorySearchQuery && showCategoryDropdown && categories.length > 0 && (
                                                            <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded shadow-lg z-10 max-h-48 overflow-y-auto">
                                                                {categories.map((cat) => (
                                                                    <button
                                                                        key={cat.id}
                                                                        onClick={() => {
                                                                            setSelectedCategory(cat);
                                                                            setCategorySearchQuery('');
                                                                            setShowCategoryDropdown(false);
                                                                        }}
                                                                        className="w-full text-left px-3 py-2 hover:bg-blue-100 dark:hover:bg-blue-900 text-sm text-gray-900 dark:text-white"
                                                                    >
                                                                        {cat.hierarchy && cat.hierarchy.length > 0 ? (
                                                                            <div className="flex flex-col gap-0.5">
                                                                                <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                                                    {cat.hierarchy.map((h, idx) => (
                                                                                        <span key={h.id}>
                                                                                            {idx > 0 && ' / '}
                                                                                            <span className="font-medium text-gray-700 dark:text-gray-300">{h.name}</span>
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                                <div className="text-sm font-medium">
                                                                                    {cat.hierarchy[cat.hierarchy.length - 1]?.name || cat.name}
                                                                                </div>
                                                                            </div>
                                                                        ) : (
                                                                            <div>
                                                                                <span className="font-medium">{cat.code}</span>
                                                                                {cat.name && <span className="text-gray-600 dark:text-gray-400"> - {cat.name}</span>}
                                                                            </div>
                                                                        )}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>

                                                    {selectedCategory && (
                                                        <div className="mb-3 p-3 bg-blue-100 dark:bg-blue-900/40 rounded text-sm">
                                                            <div className="text-blue-900 dark:text-blue-100">
                                                                <div className="font-semibold mb-2">Kiválasztva:</div>
                                                                {selectedCategory.hierarchy && selectedCategory.hierarchy.length > 0 ? (
                                                                    <div className="flex flex-col gap-1">
                                                                        <div className="flex items-center gap-1.5 text-xs">
                                                                            {selectedCategory.hierarchy.map((h, idx) => (
                                                                                <span key={h.id}>
                                                                                    {idx > 0 && <span className="text-blue-600 dark:text-blue-300">/</span>}
                                                                                    <span className="font-semibold">{h.name}</span>
                                                                                </span>
                                                                            ))}
                                                                        </div>
                                                                        <div className="text-sm">
                                                                            {selectedCategory.hierarchy[selectedCategory.hierarchy.length - 1]?.name}
                                                                        </div>
                                                                    </div>
                                                                ) : (
                                                                    <div>
                                                                        <div className="font-semibold">{selectedCategory.code}</div>
                                                                        {selectedCategory.name && <div>{selectedCategory.name}</div>}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Márka kiválasztása */}
                                                    {staging.product_data.brand_name && (
                                                        <div className="mb-3">
                                                            <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                                                Márka - "{staging.product_data.brand_name}"
                                                            </label>
                                                            <div className="relative mb-3">
                                                                <div className="flex gap-2">
                                                                    <input
                                                                        type="text"
                                                                        placeholder="Márka keresése vagy új létrehozása..."
                                                                        value={brandSearchQuery}
                                                                        onChange={(e) => setBrandSearchQuery(e.target.value)}
                                                                        className="flex-1 px-3 py-2 border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                                                                    />
                                                                    {selectedBrand && (
                                                                        <button
                                                                            onClick={() => {
                                                                                setSelectedBrand(null);
                                                                                setBrandSearchQuery('');
                                                                            }}
                                                                            className="px-2 py-2 hover:bg-blue-200 dark:hover:bg-blue-800 rounded"
                                                                        >
                                                                            <X className="h-4 w-4" />
                                                                        </button>
                                                                    )}
                                                                </div>

                                                                {brandSearchQuery && showBrandDropdown && (
                                                                    <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 border border-blue-300 dark:border-blue-700 rounded shadow-lg z-10 max-h-48 overflow-y-auto">
                                                                        {brands
                                                                            .filter(b =>
                                                                                b.name.toLowerCase().includes(brandSearchQuery.toLowerCase())
                                                                            )
                                                                            .map((brand) => (
                                                                                <button
                                                                                    key={brand.id}
                                                                                    onClick={() => {
                                                                                        setSelectedBrand(brand);
                                                                                        setBrandSearchQuery('');
                                                                                        setShowBrandDropdown(false);
                                                                                    }}
                                                                                    className="w-full text-left px-3 py-2 hover:bg-blue-100 dark:hover:bg-blue-900 text-sm text-gray-900 dark:text-white"
                                                                                >
                                                                                    {brand.name}
                                                                                </button>
                                                                            ))
                                                                        }
                                                                        {!brands.some(b =>
                                                                            b.name.toLowerCase() === brandSearchQuery.toLowerCase()
                                                                        ) && (
                                                                                <button
                                                                                    onClick={handleCreateBrand}
                                                                                    disabled={creatingBrand}
                                                                                    className="w-full text-left px-3 py-2 hover:bg-green-100 dark:hover:bg-green-900 text-sm text-green-700 dark:text-green-300 font-medium border-t border-blue-300 dark:border-blue-700"
                                                                                >
                                                                                    {creatingBrand ? 'Létrehozás...' : `+ Új: "${brandSearchQuery}"`}
                                                                                </button>
                                                                            )}
                                                                    </div>
                                                                )}
                                                            </div>

                                                            {selectedBrand && (
                                                                <div className="mb-3 p-3 bg-blue-100 dark:bg-blue-900/40 rounded text-sm">
                                                                    <div className="text-blue-900 dark:text-blue-100">
                                                                        <div className="font-semibold">{selectedBrand.name}</div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Bolt kiválasztása */}
                                                    <div className="mb-3">
                                                        <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                                            Bolt (opcionális)
                                                        </label>
                                                        <select
                                                            value={selectedShop?.id || ''}
                                                            onChange={(e) => {
                                                                const shop = shops.find(s => s.id === parseInt(e.target.value));
                                                                setSelectedShop(shop || null);
                                                            }}
                                                            className="w-full px-3 py-2 border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                                                        >
                                                            <option value="">Válassz boltot...</option>
                                                            {shops.map((shop) => (
                                                                <option key={shop.id} value={shop.id}>
                                                                    {shop.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>

                                                    {/* Mértékegység kiválasztása */}
                                                    <div className="mb-3">
                                                        <label className="block text-sm font-medium text-blue-900 dark:text-blue-100 mb-2">
                                                            Mértékegység <span className="text-red-600">*</span>
                                                        </label>
                                                        <select
                                                            value={selectedUnit?.id || suggestedUnit?.id || ''}
                                                            onChange={(e) => {
                                                                const unit = units.find(u => u.id === parseInt(e.target.value));
                                                                setSelectedUnit(unit || null);
                                                            }}
                                                            className="w-full px-3 py-2 border border-blue-300 dark:border-blue-700 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                                                        >
                                                            <option value="">Válassz mértékegységet...</option>
                                                            {units.map((unit) => (
                                                                <option key={unit.id} value={unit.id}>
                                                                    {unit.name} ({unit.abbreviation})
                                                                    {suggestedUnit?.id === unit.id ? ' ← javasolt' : ''}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {suggestedUnit && !selectedUnit && (
                                                            <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                                                Javasolt a csomagolás alapján: {suggestedUnit.name} ({suggestedUnit.abbreviation})
                                                            </p>
                                                        )}
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleCreateProduct(staging)}
                                                            disabled={!selectedCategory || creatingProduct || !!staging.existing_product}
                                                            title={staging.existing_product ? "Ez a termék már létezik! Nem lehet létrehozni." : ""}
                                                            className="flex-1 px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded text-sm font-medium flex items-center justify-center gap-2"
                                                        >
                                                            {creatingProduct ? (
                                                                <>
                                                                    <Loader className="h-4 w-4 animate-spin" />
                                                                    Létrehozás...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <Plus className="h-4 w-4" />
                                                                    Termék létrehozása
                                                                </>
                                                            )}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEditingStaging(null);
                                                                setSelectedCategory(null);
                                                                setCategorySearchQuery('');
                                                                setSelectedShop(null);
                                                                setSelectedUnit(null);
                                                                setSelectedBrand(null);
                                                                setBrandSearchQuery('');
                                                                setSuggestedUnit(null);
                                                            }}
                                                            className="px-3 py-2 bg-gray-300 dark:bg-gray-700 hover:bg-gray-400 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded text-sm font-medium"
                                                        >
                                                            Mégsem
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {editingStaging !== staging.id && (
                                                <button
                                                    onClick={() => {
                                                        setEditingStaging(staging.id);
                                                        setSelectedCategory(null);
                                                        setCategorySearchQuery('');
                                                        setSelectedShop(null);
                                                        setSelectedUnit(null);
                                                        setSelectedBrand(null);
                                                        setBrandSearchQuery('');

                                                        // Auto-fill brand if exists
                                                        if (staging.product_data.brand_name) {
                                                            const existingBrand = brands.find(b =>
                                                                b.name.toLowerCase() === staging.product_data.brand_name?.toLowerCase()
                                                            );
                                                            if (existingBrand) {
                                                                setSelectedBrand(existingBrand);
                                                            } else {
                                                                setBrandSearchQuery(staging.product_data.brand_name);
                                                            }
                                                        }

                                                        // Auto-suggest unit from packaging
                                                        const suggested = getSuggestedUnitFromPackaging(staging);
                                                        setSuggestedUnit(suggested);
                                                        if (suggested) {
                                                            setSelectedUnit(suggested);
                                                        }
                                                    }}
                                                    className="w-full px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium flex items-center justify-center gap-2 mt-3"
                                                >
                                                    <Plus className="h-4 w-4" />
                                                    Létrehoz
                                                </button>
                                            )}
                                        </div>
                                    )}

                                    {/* Promóció adatok */}
                                    {Object.keys(staging.promotion_data).length > 0 && (
                                        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Tag className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                                                <h3 className="font-semibold text-purple-900 dark:text-purple-100">Promóció adatok</h3>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                {Object.entries(staging.promotion_data).map(([key, value]) => {
                                                    let displayValue = String(value);
                                                    if (key.includes('_at') && value) {
                                                        try {
                                                            // Convert Unix timestamp (number) or ISO string to date
                                                            const timestamp = typeof value === 'number' ? value * 1000 : value;
                                                            displayValue = new Date(timestamp).toLocaleString('hu-HU');
                                                        } catch (e) {
                                                            // Keep original value
                                                        }
                                                    }
                                                    return (
                                                        <div key={key}>
                                                            <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                                                            <span className="ml-1 font-medium text-gray-900 dark:text-white">{displayValue}</span>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Csomagolás adatok */}
                                    {Object.keys(staging.packaging_data).length > 0 && (
                                        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Box className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                                                <h3 className="font-semibold text-amber-900 dark:text-amber-100">Csomagolás adatok</h3>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                {Object.entries(staging.packaging_data).map(([key, value]) => (
                                                    <div key={key}>
                                                        <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                                                        <span className="ml-1 font-medium text-gray-900 dark:text-white">{String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Árak */}
                                    {staging.prices_data.length > 0 && (
                                        <div className="bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <DollarSign className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                                                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">Árak ({staging.prices_data.length})</h3>
                                            </div>
                                            <div className="space-y-2">
                                                {staging.prices_data.map((price, idx) => (
                                                    <div key={idx} className="bg-white dark:bg-gray-800 p-2 rounded text-sm">
                                                        <p className="font-medium text-gray-900 dark:text-white">{price.price_type_code}: {price.value} {staging.promotion_data.currency || 'EUR'}</p>
                                                        {price.description && <p className="text-gray-600 dark:text-gray-400">{price.description}</p>}
                                                        {(price.starts_at || price.ends_at) && (
                                                            <p className="text-xs text-gray-500 dark:text-gray-500">
                                                                {price.starts_at} - {price.ends_at}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Deposzt adatok */}
                                    {Object.keys(staging.deposit_data).length > 0 && (
                                        <div className="bg-cyan-50 dark:bg-cyan-900/20 rounded-lg p-4">
                                            <div className="flex items-center gap-2 mb-3">
                                                <Droplet className="h-4 w-4 text-cyan-600 dark:text-cyan-400" />
                                                <h3 className="font-semibold text-cyan-900 dark:text-cyan-100">Deposzt adatok</h3>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                {Object.entries(staging.deposit_data).map(([key, value]) => (
                                                    <div key={key}>
                                                        <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                                                        <span className="ml-1 font-medium text-gray-900 dark:text-white">{String(value)}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Hiba üzenet */}
                                    {staging.error_message && (
                                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                                            <p className="text-sm text-red-800 dark:text-red-200">
                                                <span className="font-semibold">Hiba:</span> {staging.error_message}
                                            </p>
                                        </div>
                                    )}

                                    {/* Metaadatok */}
                                    <div className="pt-3 border-t border-gray-200 dark:border-gray-700 grid grid-cols-3 gap-2 text-xs text-gray-600 dark:text-gray-400">
                                        <div>
                                            <span className="font-medium">Forrás:</span> {staging.source}
                                        </div>
                                        <div>
                                            <span className="font-medium">Létrehozva:</span> {new Date(staging.created_at).toLocaleString('hu-HU')}
                                        </div>
                                        {staging.created_by && (
                                            <div>
                                                <span className="font-medium">Készítette:</span> {staging.created_by}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Összefoglaló */}
            <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Összesen: <span className="font-semibold">{stagings.length}</span> staging adat
                </p>
            </div>
        </div>
    );
};

// Box icon ha nincs importálva
const Box = ({ className = '' }: { className?: string }) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m0 0l8 4m-8-4v10l8 4m0-10l8-4m-8 4v10l8-4M9 5l3 1.5m3-1.5L9 5" />
    </svg>
);

export default ExtPromotions;
